// cspell:ignore initialised
import { checkAuth, Connections, getUserId, initConnection, initTerra } from 'terra-react'
import { Analytics, Bugsnag, CustomEventTypes, Device } from '@src/config'
import { SUFFICIENT_PERMISSIONS } from '@src/services/Terra/permissions'
import { getDevId } from '@src/utils/terra'
import { withTerraLock } from '@src/utils/terraLock'
import Storage from '@src/utils/storage'

const inFlightInitByRequest = new Map<string, Promise<{ success: boolean; userId?: string }>>()

const HEALTH_CONNECT_UNAVAILABLE_ERROR_MESSAGE = 'Health Connect is not available on this device'
// Emitted by the native Terra SDK when initConnection runs against a TerraManager
// instance whose async initialization hasn't finished or has failed
const TERRA_NOT_INITIALIZED_ERROR_MESSAGE = 'Terra class is initialised properly'

const notifyHealthConnectError = (
  errorMessage: string,
  metadata: Record<string, string | boolean | number | null | undefined>,
) => {
  Bugsnag.notify(errorMessage, (event) => {
    event.addMetadata('terraHealthConnect', metadata)
  })
}

// Terra's native class holds no persistent state and must be re-initialized on every
// app open/foreground, so we always call initTerra before connecting rather than caching
// an "already initialized" flag in JS. A stale JS flag outlives the native Terra instance
// (e.g. after Android reclaims the activity in the background), which made initConnection
// fail with the native "Terra class is not initialized" error.
// See https://docs.tryterra.co/health-and-fitness-api/mobile-only-sources/react-native
const initialize = async (userId: string) => {
  const devId = getDevId()

  const initializationResult = await initTerra(devId, userId)

  if (!initializationResult.success || initializationResult.error) {
    notifyHealthConnectError(
      `Failed to initialize Terra SDK for Health Connect: ${initializationResult.error}`,
      {
        connection: Connections.HEALTH_CONNECT,
        devId,
        userIdPresent: Boolean(userId),
      },
    )
    return false
  }

  return true
}

const attemptConnection = async ({
  getToken,
  userId,
  forceNewConnection,
  finalAttempt,
}: {
  getToken: () => Promise<string>
  userId: string
  forceNewConnection: boolean
  finalAttempt: boolean
}): Promise<{ success: boolean; userId?: string; shouldRetry?: boolean }> => {
  const initializationSuccessful = await initialize(userId)
  if (!initializationSuccessful) {
    return { success: false }
  }

  if (!forceNewConnection) {
    const authState = await checkAuth(Connections.HEALTH_CONNECT, getDevId())

    if (authState.success) {
      const userIdResult = await getUserId(Connections.HEALTH_CONNECT)
      if (userIdResult.success && userIdResult.userId) {
        return { success: true, userId: userIdResult.userId }
      }
    }
  }

  const token = await getToken()

  const connectionResult = await initConnection(
    Connections.HEALTH_CONNECT,
    token,
    true,
    SUFFICIENT_PERMISSIONS,
  )

  if (connectionResult.success) {
    const userIdResult = await getUserId(Connections.HEALTH_CONNECT)
    if (userIdResult.userId) {
      Analytics.track(CustomEventTypes.TerraHealthConnectSyncEnabled, {
        source: 'New connection initialization',
      })
      Storage.remove(`${Storage.HEALTH_CONNECT_LATEST_SYNC_TIMESTAMP_KEY}_${userId}`)
      Storage.remove(`${Storage.HEALTH_CONNECT_LATEST_ATTEMPTED_SYNC_TIMESTAMP_KEY}_${userId}`)

      return { success: true, userId: userIdResult.userId }
    }

    Analytics.track(CustomEventTypes.TerraHealthConnectSyncFailed, {
      source: 'New connection initialization',
    })
    notifyHealthConnectError(
      'Failed to initialize Terra <> Health Connect connection Missing Terra user ID after successful initialization',
      {
        connection: Connections.HEALTH_CONNECT,
        devId: getDevId(),
        forceNewConnection,
        userIdPresent: Boolean(userId),
      },
    )
    return { success: false }
  }

  const terraNotInitialized =
    typeof connectionResult.error === 'string' &&
    connectionResult.error.includes(TERRA_NOT_INITIALIZED_ERROR_MESSAGE)

  if (terraNotInitialized && !finalAttempt) {
    return { success: false, shouldRetry: true }
  }

  Analytics.track(CustomEventTypes.TerraHealthConnectSyncFailed, {
    source: 'New connection initialization',
  })
  if (
    typeof connectionResult.error !== 'string' ||
    !connectionResult.error.includes(HEALTH_CONNECT_UNAVAILABLE_ERROR_MESSAGE)
  ) {
    notifyHealthConnectError(
      `Failed to initialize Terra <> Health Connect connection ${connectionResult.error}`,
      {
        connection: Connections.HEALTH_CONNECT,
        devId: getDevId(),
        forceNewConnection,
        userIdPresent: Boolean(userId),
        tokenPresent: Boolean(token),
        tokenLength: token.length,
        retried: finalAttempt,
      },
    )
  }

  return { success: false }
}

const performInitializeConnection = ({
  getToken,
  userId,
  forceNewConnection,
}: {
  getToken: () => Promise<string>
  userId: string
  forceNewConnection: boolean
}): Promise<{ success: boolean; userId?: string }> => {
  if (Device.web) {
    return Promise.resolve({ success: false })
  }

  return withTerraLock(async () => {
    const firstAttempt = await attemptConnection({
      getToken,
      userId,
      forceNewConnection,
      finalAttempt: false,
    })

    if (!firstAttempt.shouldRetry) {
      return { success: firstAttempt.success, userId: firstAttempt.userId }
    }

    // The Terra instance created by our initTerra died or was replaced before
    // initConnection could use it; a second pass with a fresh instance recovers this
    return attemptConnection({ getToken, userId, forceNewConnection, finalAttempt: true })
  })
}

const initializeConnection = ({
  getToken,
  userId,
  forceNewConnection,
}: {
  getToken: () => Promise<string>
  userId: string
  forceNewConnection: boolean
}) => {
  const dedupeKey = `${userId}:${forceNewConnection ? 'force' : 'standard'}`
  const inFlightInitialization = inFlightInitByRequest.get(dedupeKey)
  if (inFlightInitialization) {
    return inFlightInitialization
  }

  const initializationPromise = performInitializeConnection({
    getToken,
    userId,
    forceNewConnection,
  }).finally(() => {
    if (inFlightInitByRequest.get(dedupeKey) === initializationPromise) {
      inFlightInitByRequest.delete(dedupeKey)
    }
  })

  inFlightInitByRequest.set(dedupeKey, initializationPromise)

  return initializationPromise
}

export default {
  initializeConnection,
}
